/**
 * Wrappers tipados ao redor do `newApi` axios instance. Resolvem dois problemas:
 *
 * 1. **Envelope inconsistente**: backend mistura `BaseController.ok` (`{status, message, data}`)
 *    com respostas diretas. Esses helpers auto-unwrap a envelope quando presente.
 *
 * 2. **Generics tipados**: `newApi.get<T>` tipa `response.data` como `T`, mas o caller
 *    ainda precisa fazer `response.data.data` quando há envelope. Esses helpers expõem
 *    diretamente o `T` desempacotado.
 *
 * Use SEMPRE no boundary (`contexts/*\/utils.ts`) em vez de `newApi.get` direto quando
 * o endpoint usa `BaseController.ok`. Para endpoints `jsonResponse()` direto
 * (`/auth/refresh`, `/auth/login`), continue usando `newApi.get`/`.post` direto.
 */

import type { AxiosRequestConfig } from "axios";
import newApi from "./api";
import { unwrapEnvelope } from "../normalizers";

/**
 * Envelope padrão do backend BaseController.ok: `{ data: T, status, message }`.
 * Use com `new_api.get<IEnvelope<X>>(...)` quando o caller acessa `response.data.data` direto.
 * Helpers `apiGet`/`apiPost` neste mesmo arquivo já fazem auto-unwrap quando preferível.
 */
export type IEnvelope<T> = {
  data: T;
  status?: string;
  message?: string;
};

/** GET tipado com envelope auto-unwrap. */
export async function apiGet<TData>(
  url: string,
  config?: AxiosRequestConfig
): Promise<TData> {
  const response = await newApi.get(url, config);
  return unwrapEnvelope<TData>(response.data);
}

/** POST tipado com envelope auto-unwrap. */
export async function apiPost<TData, TBody = unknown>(
  url: string,
  body?: TBody,
  config?: AxiosRequestConfig
): Promise<TData> {
  const response = await newApi.post(url, body, config);
  return unwrapEnvelope<TData>(response.data);
}

/** PUT tipado com envelope auto-unwrap. */
export async function apiPut<TData, TBody = unknown>(
  url: string,
  body?: TBody,
  config?: AxiosRequestConfig
): Promise<TData> {
  const response = await newApi.put(url, body, config);
  return unwrapEnvelope<TData>(response.data);
}

/** PATCH tipado com envelope auto-unwrap. */
export async function apiPatch<TData, TBody = unknown>(
  url: string,
  body?: TBody,
  config?: AxiosRequestConfig
): Promise<TData> {
  const response = await newApi.patch(url, body, config);
  return unwrapEnvelope<TData>(response.data);
}

/** DELETE tipado com envelope auto-unwrap. */
export async function apiDelete<TData>(
  url: string,
  config?: AxiosRequestConfig
): Promise<TData> {
  const response = await newApi.delete(url, config);
  return unwrapEnvelope<TData>(response.data);
}
